import { useState } from 'react';

interface GenerateInstructionsParams {
  spec: string | null;
  projectName: string;
}

// Azure OpenAI Configuration from environment variables
const AZURE_CONFIG = {
  endpoint: (import.meta.env.VITE_AZURE_OPENAI_ENDPOINT || '').replace(/\/+$/, ''),
  apiKey: import.meta.env.VITE_AZURE_OPENAI_API_KEY || '',
  deploymentName: (import.meta.env.VITE_AZURE_OPENAI_DEPLOYMENT_NAME || 'o4-mini').trim(),
  apiVersion: (import.meta.env.VITE_AZURE_OPENAI_API_VERSION || '2025-01-01-preview').trim()
};

export const useAzureOpenAI = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useMock, setUseMock] = useState(false);

  const generateInstructions = async ({ spec, projectName }: GenerateInstructionsParams): Promise<string> => {
    setIsGenerating(true);
    setError(null);

    try {
      const isSpecEmpty = !spec || spec.trim() === '';
      
      // Prepare the prompt
      const systemPrompt = `You are an AI assistant helping create clear, comprehensive annotation instructions for data labeling projects. Format your response in markdown with proper headings, lists, and emphasis where appropriate.`;
      
      const userPrompt = isSpecEmpty 
        ? `Generate example annotation instructions for a sample image classification task. 
           Start with: "**Note: The spec document was successfully retrieved but was empty. Here are example instructions for demonstration purposes.**"
           
           Include sections for: Overview, Task Description, Guidelines, Edge Cases, Quality Standards, and Common Mistakes.`
        : `Given this project specification for a data annotation task:
           ${spec}
           
           Generate comprehensive instructions for annotators that include:
           1. Task overview and objectives
           2. Step-by-step annotation guidelines
           3. Edge cases and special considerations
           4. Quality standards and expectations
           5. Common mistakes to avoid
           
           Format the output in clear sections with markdown formatting.`;

      try {
        // Check if Azure OpenAI is configured
        if (!AZURE_CONFIG.endpoint || !AZURE_CONFIG.apiKey) {
          console.warn('Azure OpenAI not configured. Using mock data.');
          throw new Error('Azure OpenAI credentials not configured');
        }

        // Try real Azure OpenAI API call
        console.log('🤖 Calling Azure OpenAI API...');
        
        const response = await fetch(
          `${AZURE_CONFIG.endpoint}/openai/deployments/${AZURE_CONFIG.deploymentName}/chat/completions?api-version=${AZURE_CONFIG.apiVersion}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-key': AZURE_CONFIG.apiKey
            },
            body: JSON.stringify({
              messages: [
                { role: 'system', content: systemPrompt },
                { role: 'user', content: userPrompt }
              ],
              max_completion_tokens: 4000
            })
          }
        );

        if (!response.ok) {
          const errorData = await response.text();
          console.error('Azure OpenAI API error:', response.status, errorData);
          throw new Error(`API call failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Successfully generated instructions using Azure OpenAI');
        return data.choices[0].message.content;

      } catch (apiError) {
        console.error('Failed to call Azure OpenAI, falling back to mock:', apiError);
        setUseMock(true);
        
        // Fallback to mock implementation
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate delay
        
        if (isSpecEmpty) {
        return `**Note: The spec document was successfully retrieved but was empty. Here are example instructions for demonstration purposes.**

# Annotation Instructions for ${projectName}

## Overview
This document provides comprehensive guidelines for annotating data in the ${projectName} project. Please read through all sections carefully before beginning your annotation work.

## Task Description
Your task is to review and annotate data according to the following criteria:

### Primary Objectives
1. **Accuracy**: Ensure all annotations are precise and correct
2. **Consistency**: Apply the same standards across all data points
3. **Completeness**: Annotate all required fields for each item

## Annotation Guidelines

### Step 1: Initial Review
- Open the annotation interface
- Review the data item carefully
- Identify key elements that need annotation

### Step 2: Apply Labels
- Select appropriate labels from the provided taxonomy
- Ensure labels accurately represent the content
- Use the "Uncertain" option when classification is ambiguous

### Step 3: Quality Check
- Review your annotations before submission
- Verify all required fields are completed
- Check for consistency with previous annotations

## Edge Cases

### Ambiguous Content
When encountering ambiguous content:
- Document the ambiguity in the notes field
- Apply the most likely label
- Flag for review if necessary

### Missing Information
If critical information is missing:
- Mark the item as "Incomplete"
- Add a note explaining what information is missing
- Move to the next item

## Quality Standards

- **Accuracy Rate**: Maintain >95% accuracy
- **Consistency**: Inter-annotator agreement should be >90%
- **Completeness**: 100% of required fields must be filled

## Common Mistakes to Avoid

1. **Rushing through items** - Take time to review each item carefully
2. **Inconsistent labeling** - Refer to guidelines when uncertain
3. **Skipping edge cases** - Document and handle all edge cases properly
4. **Ignoring context** - Consider the full context when annotating

## Support and Resources

If you need assistance:
- Contact your project manager
- Review the FAQ section
- Join the annotation team Slack channel

Remember: Quality is more important than quantity. Take your time to ensure accurate annotations.`;
      }

      // Generate based on actual spec
      return `# Annotation Instructions for ${projectName}

## Project Overview
Based on the provided specification:

${spec}

## Detailed Annotation Guidelines

### Getting Started
1. Familiarize yourself with the project specification above
2. Access the annotation interface using your credentials
3. Complete the training module before starting actual annotations

### Annotation Process

#### Step 1: Data Review
- Carefully examine each data item
- Identify all elements requiring annotation
- Note any ambiguities or special cases

#### Step 2: Applying Annotations
- Use the annotation tools as specified in the project requirements
- Follow the labeling taxonomy strictly
- Maintain consistency across all annotations

#### Step 3: Quality Assurance
- Self-review each annotation before submission
- Ensure all required fields are completed
- Verify annotations align with project specifications

### Best Practices

1. **Consistency is Key**
   - Apply the same criteria throughout
   - Refer to examples when uncertain
   - Document your decision-making process

2. **Attention to Detail**
   - Review all aspects of each item
   - Don't make assumptions
   - Ask for clarification when needed

3. **Time Management**
   - Pace yourself appropriately
   - Take regular breaks to maintain focus
   - Quality over speed

### Common Scenarios and Solutions

**Scenario 1: Ambiguous Data**
- Document the ambiguity
- Apply the most appropriate label
- Flag for review

**Scenario 2: Technical Issues**
- Save your work frequently
- Report issues immediately
- Keep backup notes

### Quality Metrics

Your work will be evaluated based on:
- Accuracy (>95% required)
- Consistency (>90% inter-annotator agreement)
- Completeness (100% of required fields)
- Timeliness (meeting project deadlines)

### Resources and Support

- Project documentation: [Link]
- FAQ section: [Link]
- Support contact: [Email/Slack]
- Training materials: [Link]

Remember to maintain high quality standards throughout the project. Your careful attention to detail ensures the success of this project.`;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate instructions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateInstructions,
    isGenerating,
    error,
    useMock
  };
};